/**
 * Current-user + logout hooks (PHASE1 §7.4).
 *
 * `useCurrentUser` reads the user resolved by `AuthGate` from `UserContext`
 * (no re-fetch). `useLogout` POSTs `/api/auth/logout` (with the CSRF header via
 * the shared transport) and reloads so `AuthGate` re-runs and the now-missing
 * session redirects to the OIDC login.
 */
import { use, useCallback, useState } from 'react';
import { api } from '../api.ts';
import { UserContext, type CurrentUser } from '../../context/UserContext.tsx';

export function useCurrentUser(): CurrentUser {
  const user = use(UserContext);
  if (!user) {
    throw new Error('useCurrentUser muss innerhalb von <UserProvider> verwendet werden.');
  }
  return user;
}

export function useLogout(): { logout: () => Promise<void>; isLoggingOut: boolean } {
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const logout = useCallback(async () => {
    setIsLoggingOut(true);
    try {
      await api.post('/api/auth/logout').catch(() => {
        // Even if the request fails, fall through to the reload → login flow.
      });
    } finally {
      window.location.reload();
    }
  }, []);

  return { logout, isLoggingOut };
}
